/* ==========================================================================
   TRIUNGULO LAB — SHOPIFY PRODUCT PAGE SCRIPT
   - Dynamic Canvas Color Extraction
   - Drifting Blob Particle Simulation
   - Cursor-Reactive Specular Highlights
   - Liquid Variant Selector & Preorder Engine
   - Synchronized Hero & Gallery Media
   - Cinematic Multi-Video Switcher
   - Feature Hotspots (Desktop Tooltip + Mobile Sheet)
   - Related Products Smooth Carousel
   - Shopify AJAX Cart Integration
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  initProductPage();
});

function initProductPage() {
  initHeroTitle();
  initBlobField();
  initGlassPointer();
  initGallery();
  initVariants();
  initQuantity();
  initAddToCart();
  initScrollReveal();
  initDescriptionReveal();
  initHotspots();
  initVideoSwitcher();
  initSuggestionCarousel();
  initImageCardTints();
  initRecommendationCardTints();
  balanceCardDescriptions();
  cleanRichTextMetafields();
  initDescriptionMediaMobile();
  initShortDescHover();
  initUserManualSync();

  // 1. Immediately apply base page backdrop blobs if configured in metafield
  const initialBlobConfig = readBlobConfig();
  if (initialBlobConfig) {
    if (initialBlobConfig.base_bg_color) {
      document.documentElement.style.setProperty('--page-bg', initialBlobConfig.base_bg_color);
    }
    if (Array.isArray(initialBlobConfig.blob_colors) && initialBlobConfig.blob_colors.length >= 1) {
      const bgColors = initialBlobConfig.blob_colors.map(parseColorToRgb).filter(Boolean);
      if (bgColors.length >= 1) {
        const spans = document.querySelectorAll('.page-backdrop span');
        const spreadList = spread(bgColors, Math.max(spans.length, 8));
        PALETTES['page'] = spreadList.map(rgbCss);
        spans.forEach((el, i) => {
          el.style.background = PALETTES['page'][i % PALETTES['page'].length];
        });
      }
    }
  }

  // Blob palette initialization:
  // 1. If manual overrides are configured via section settings or metafield, apply them immediately
  const manualColors = readManualBlobColors();
  if (manualColors && manualColors.length) {
    applyPalette(manualColors, true);
  } else {
    // 2. Otherwise extract dominant authentic colors from product photo
    const mainImg = document.getElementById('galleryMainImage') || document.getElementById('heroProduct');
    if (mainImg) {
      refreshPaletteFromElement(mainImg);
    }
  }
}

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ==========================================================================
   01. HERO TITLE ENTRANCE & BALANCED MULTI-ROW SPLIT
   Distributes title across maximum 3 rows:
   - 1 row for short/manageable titles
   - 2 rows balanced by character count
   - 3 rows maximum, evenly divided
   Each row is enclosed in a .hero-row container to prevent unwanted wrapping.
   ========================================================================== */
function partitionWords(words, numRows) {
  if (numRows <= 1 || words.length <= 1) return [words];
  if (numRows === 2) {
    let bestSplit = 1;
    let minDiff = Infinity;
    const lens = words.map(w => w.length);
    for (let i = 1; i < words.length; i++) {
      const len1 = lens.slice(0, i).reduce((a, b) => a + b, 0);
      const len2 = lens.slice(i).reduce((a, b) => a + b, 0);
      const diff = Math.abs(len1 - len2);
      if (diff < minDiff) {
        minDiff = diff;
        bestSplit = i;
      }
    }
    return [words.slice(0, bestSplit), words.slice(bestSplit)];
  }
  // numRows === 3
  if (words.length === 2) return [[words[0]], [words[1]]];
  let bestI = 1, bestJ = 2;
  let minSpread = Infinity;
  const lens = words.map(w => w.length);
  for (let i = 1; i < words.length - 1; i++) {
    for (let j = i + 1; j < words.length; j++) {
      const len1 = lens.slice(0, i).reduce((a, b) => a + b, 0);
      const len2 = lens.slice(i, j).reduce((a, b) => a + b, 0);
      const len3 = lens.slice(j).reduce((a, b) => a + b, 0);
      const spread = Math.max(len1, len2, len3) - Math.min(len1, len2, len3);
      if (spread < minSpread) {
        minSpread = spread;
        bestI = i;
        bestJ = j;
      }
    }
  }
  return [words.slice(0, bestI), words.slice(bestI, bestJ), words.slice(bestJ)];
}

function initHeroTitle() {
  const hero = document.getElementById('productHero');
  const title = document.getElementById('heroTitle');
  if (!hero || !title) return;

  // Gather raw text from aria-label (set by Liquid) or inner text
  const rawText = (title.getAttribute('aria-label') || title.textContent).trim();
  const words = rawText.split(/\s+/).filter(Boolean);
  if (!words.length) return;

  // Decide how many rows (1, 2, or maximum 3)
  const totalChars = words.reduce((s, w) => s + w.length, 0);
  let targetRows = 1;
  if (words.length >= 2 && totalChars > 14) targetRows = 2;
  if (words.length >= 3 && totalChars > 26) targetRows = 3;
  if (words.length >= 5) targetRows = 3;
  targetRows = Math.min(3, targetRows);

  const rowGroups = partitionWords(words, targetRows);

  // Re-render the title with dedicated row wrappers
  title.innerHTML = '';
  title.setAttribute('aria-label', rawText);
  let charIndex = 0;

  rowGroups.forEach((rowWords) => {
    const rowEl = document.createElement('span');
    rowEl.className = 'hero-row';
    rowWords.forEach((word) => {
      const w = document.createElement('span');
      w.className = 'hero-word';
      [...word].forEach((ch) => {
        const c = document.createElement('span');
        c.className = 'hero-char';
        c.textContent = ch;
        c.style.setProperty('--i', String(charIndex++));
        w.appendChild(c);
      });
      rowEl.appendChild(w);
    });
    title.appendChild(rowEl);
  });

  const fitHeroTitle = () => {
    title.style.fontSize = '';
    const container = title.parentElement || title;
    const maxAllowedWidth = Math.min(window.innerWidth - 36, (container.clientWidth > 0 ? container.clientWidth - 24 : window.innerWidth - 36));
    if (maxAllowedWidth <= 0) return;

    const rows = title.querySelectorAll('.hero-row');
    let maxRowWidth = 0;
    rows.forEach((r) => {
      const w = Math.max(r.scrollWidth || 0, r.getBoundingClientRect().width || 0);
      if (w > maxRowWidth) maxRowWidth = w;
    });

    if (maxRowWidth > maxAllowedWidth) {
      const currentFontSize = parseFloat(window.getComputedStyle(title).fontSize) || 36;
      const ratio = (maxAllowedWidth / maxRowWidth) * 0.95;
      title.style.fontSize = Math.max(16, currentFontSize * ratio).toFixed(1) + 'px';
    }
  };

  fitHeroTitle();
  window.addEventListener('resize', fitHeroTitle, { passive: true });
  window.addEventListener('orientationchange', fitHeroTitle, { passive: true });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(fitHeroTitle).catch(() => {});
  }

  const charCount = title.querySelectorAll('.hero-char').length;
  const settle = 260 + charCount * 38 + 700;

  const reveal = () => {
    hero.classList.add('loaded');
    setTimeout(() => hero.classList.add('emptied'), settle);
  };

  if (!prefersReducedMotion()) {
    requestAnimationFrame(() => setTimeout(reveal, 260));
  } else {
    hero.classList.add('loaded', 'emptied');
  }
}

/* ==========================================================================
   02. DYNAMIC COLOR EXTRACTION ENGINE
   Analyzes genuine product image colors with minimum quantity thresholds,
   filtering out white/light backgrounds and near-blacks.
   Applies the authentic product palette directly to blobs without artificial teal wash.
   ========================================================================== */
function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > .5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  if (s === 0) { const v = Math.round(l * 255); return { r: v, g: v, b: v }; }
  const q = l < .5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t) => {
    t = (t + 1) % 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return {
    r: Math.round(hue(h + 1 / 3) * 255),
    g: Math.round(hue(h) * 255),
    b: Math.round(hue(h - 1 / 3) * 255)
  };
}

const rgbCss = ({ r, g, b }, a) => a == null ? `rgb(${r} ${g} ${b})` : `rgb(${r} ${g} ${b} / ${a})`;

function extractDominantColors(img, count = 5) {
  try {
    const canvas = document.createElement('canvas');
    const w = 96;
    const h = Math.max(1, Math.round(w * (img.naturalHeight || img.height || 1) / (img.naturalWidth || img.width || 1)));
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const getPx = (x, y) => {
      const idx = (y * w + x) * 4;
      return [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
    };

    // 1. Sample corners and edges to establish the background color profile
    const bgSamples = [
      getPx(1, 1), getPx(w - 2, 1),
      getPx(1, h - 2), getPx(w - 2, h - 2),
      getPx(Math.floor(w / 2), 1), getPx(Math.floor(w / 2), h - 2)
    ].filter(p => p[3] >= 120);

    let bgR = 240, bgG = 240, bgB = 240;
    if (bgSamples.length) {
      bgR = bgSamples.reduce((s, p) => s + p[0], 0) / bgSamples.length;
      bgG = bgSamples.reduce((s, p) => s + p[1], 0) / bgSamples.length;
      bgB = bgSamples.reduce((s, p) => s + p[2], 0) / bgSamples.length;
    }

    const buckets = new Map();
    let validPixels = 0;

    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a < 120) continue;

      const r = data[i], g = data[i + 1], b = data[i + 2];
      const { s, l } = rgbToHsl({ r, g, b });

      // Discard background pixels matching corner profile
      const distBg = Math.hypot(r - bgR, g - bgG, b - bgB);
      if (distBg < 40) continue;

      // Discard white / light neutral backgrounds
      if ((r > 220 && g > 220 && b > 220) || (l > 0.85 && s < 0.20)) continue;
      // Discard pitch black shadow voids
      if (l < 0.04) continue;

      validPixels++;
      // 4-bit per channel quantization
      const key = `${r >> 4},${g >> 4},${b >> 4}`;
      const bucket = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0, s: 0, l: 0 };
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.n++;
      bucket.s += s;
      bucket.l += l;
      buckets.set(key, bucket);
    }

    if (!buckets.size || validPixels < 15) return null;

    const qualifying = [...buckets.values()]
      .filter((b) => b.n >= Math.max(6, validPixels * 0.008))
      .map((b) => {
        const avgR = Math.round(b.r / b.n);
        const avgG = Math.round(b.g / b.n);
        const avgB = Math.round(b.b / b.n);
        const avgS = b.s / b.n;
        const avgL = b.l / b.n;
        // Prioritize vibrant saturated tones and prominent subject mid-tones
        const score = Math.pow(b.n, 0.7) * (0.25 + avgS * 2.5 + (avgL > 0.15 && avgL < 0.75 ? 0.35 : 0));
        return { color: { r: avgR, g: avgG, b: avgB }, score, s: avgS, l: avgL };
      })
      .sort((a, b) => b.score - a.score);

    if (!qualifying.length) return null;

    const picked = [];
    for (const item of qualifying) {
      const isDistinct = picked.every((p) => {
        const dist = Math.abs(p.r - item.color.r) + Math.abs(p.g - item.color.g) + Math.abs(p.b - item.color.b);
        return dist > 55;
      });
      if (isDistinct) {
        picked.push(item.color);
      }
      if (picked.length === count) break;
    }

    return picked.length ? picked : null;
  } catch (err) {
    return null;
  }
}

/* ==========================================================================
   02B. ACTIVE VIBRANT COLOR EXTRACTION FOR RECOMMENDATION CARDS
   Strictly filters out greys, near-whites, and dull neutrals.
   Extracts and accentuates only punchy, saturated "active" colors from the
   product image or pairs them with vibrant harmonious hues for always-blurry halos.
   ========================================================================== */
function extractActiveVibrantColors(img, count = 3) {
  try {
    const canvas = document.createElement('canvas');
    const w = 96;
    const h = Math.max(1, Math.round(w * (img.naturalHeight || img.height || 1) / (img.naturalWidth || img.width || 1)));
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const getPx = (x, y) => {
      const idx = (y * w + x) * 4;
      return [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
    };

    // Sample background profile
    const bgSamples = [
      getPx(1, 1), getPx(w - 2, 1),
      getPx(1, h - 2), getPx(w - 2, h - 2),
      getPx(Math.floor(w / 2), 1), getPx(Math.floor(w / 2), h - 2)
    ].filter(p => p[3] >= 120);

    let bgR = 245, bgG = 245, bgB = 245;
    if (bgSamples.length) {
      bgR = bgSamples.reduce((s, p) => s + p[0], 0) / bgSamples.length;
      bgG = bgSamples.reduce((s, p) => s + p[1], 0) / bgSamples.length;
      bgB = bgSamples.reduce((s, p) => s + p[2], 0) / bgSamples.length;
    }

    const buckets = new Map();
    let totalValid = 0;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 120) continue;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const { s, l } = rgbToHsl({ r, g, b });

      // Discard pixels matching background
      const distBg = Math.hypot(r - bgR, g - bgG, b - bgB);
      if (distBg < 35) continue;

      // STRICTLY DISCARD GREYS AND NEUTRALS:
      // Greys have low saturation (s < 0.22)
      if (s < 0.22) continue;

      // Discard near-white and pitch black
      if (l > 0.88 || l < 0.12) continue;

      totalValid++;
      const key = `${r >> 4},${g >> 4},${b >> 4}`;
      const bucket = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0, s: 0, l: 0 };
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.n++;
      bucket.s += s;
      bucket.l += l;
      buckets.set(key, bucket);
    }

    let activeColors = [];

    if (buckets.size > 0 && totalValid >= 8) {
      const candidates = [...buckets.values()]
        .map(b => {
          const avgR = Math.round(b.r / b.n);
          const avgG = Math.round(b.g / b.n);
          const avgB = Math.round(b.b / b.n);
          const avgS = b.s / b.n;
          const avgL = b.l / b.n;
          // Heavily favor vibrant saturation and pleasing midtone luminosity
          const vibrancy = avgS * 2.8 + (1 - Math.abs(avgL - 0.50)) * 1.2;
          const score = Math.pow(b.n, 0.65) * vibrancy;
          return { color: { r: avgR, g: avgG, b: avgB }, score, s: avgS, l: avgL };
        })
        .sort((a, b) => b.score - a.score);

      for (const item of candidates) {
        const isDistinct = activeColors.every(c => {
          const dist = Math.hypot(c.r - item.color.r, c.g - item.color.g, c.b - item.color.b);
          return dist > 55;
        });
        if (isDistinct) {
          // Boost saturation of chosen color so it's always vibrant, punchy and never grey
          const hsl = rgbToHsl(item.color);
          hsl.s = Math.max(0.72, Math.min(1.0, hsl.s * 1.35));
          hsl.l = Math.max(0.40, Math.min(0.60, hsl.l));
          activeColors.push(hslToRgb(hsl));
        }
        if (activeColors.length === count) break;
      }
    }

    // Fallback if image is monochrome or has fewer active colors:
    // Pair with vibrant signature Triungulo hues
    const signaturePalette = [
      { r: 34, g: 185, b: 178 },  // Electric Cyan
      { r: 245, g: 155, b: 45 },  // Warm Amber
      { r: 76, g: 110, b: 245 },  // Royal Blue
      { r: 235, g: 90, b: 125 }   // Sunset Coral
    ];

    while (activeColors.length < count) {
      if (activeColors.length === 0) {
        activeColors.push(...signaturePalette.slice(0, count));
        break;
      }
      const baseHsl = rgbToHsl(activeColors[0]);
      const rotHsl = {
        h: (baseHsl.h + 0.38 + activeColors.length * 0.15) % 1,
        s: Math.max(0.75, baseHsl.s),
        l: Math.max(0.45, Math.min(0.60, baseHsl.l))
      };
      activeColors.push(hslToRgb(rotHsl));
    }

    return activeColors;
  } catch (err) {
    return [
      { r: 34, g: 185, b: 178 },
      { r: 245, g: 155, b: 45 },
      { r: 64, g: 140, b: 235 }
    ];
  }
}

function spread(colors, count) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const t = (i / Math.max(1, count)) * colors.length;
    const a = colors[Math.floor(t) % colors.length];
    const b = colors[(Math.floor(t) + 1) % colors.length];
    const hslA = rgbToHsl(a);
    const hslB = rgbToHsl(b);
    const mixedHsl = {
      h: hslA.h + (hslB.h - hslA.h) * (t - Math.floor(t)),
      s: hslA.s + (hslB.s - hslA.s) * (t - Math.floor(t)),
      l: hslA.l + (hslB.l - hslA.l) * (t - Math.floor(t))
    };
    mixedHsl.h = (mixedHsl.h + (i / Math.max(1, count - 1) - .5) * .075 + 1) % 1;
    out.push(hslToRgb(mixedHsl));
  }
  return out;
}

const PALETTES = { hero: [], video: [], page: [] };

// Parse hex (#fff, #ffffff) or rgb(r, g, b) string into {r, g, b}
function parseColorToRgb(str) {
  if (!str) return null;
  // If str is already a parsed RGB object, return it safely
  if (typeof str === 'object' && str !== null && 'r' in str && 'g' in str && 'b' in str) {
    return { r: Number(str.r), g: Number(str.g), b: Number(str.b) };
  }
  if (typeof str !== 'string') return null;
  str = str.trim();
  if (str.startsWith('#')) {
    const h = str.slice(1);
    if (h.length === 3) {
      return {
        r: parseInt(h[0] + h[0], 16),
        g: parseInt(h[1] + h[1], 16),
        b: parseInt(h[2] + h[2], 16)
      };
    }
    if (h.length === 6) {
      return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16)
      };
    }
  }
  const match = str.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (match) {
    return {
      r: parseInt(match[1], 10),
      g: parseInt(match[2], 10),
      b: parseInt(match[3], 10)
    };
  }
  return null;
}

// Helper to safely get the visual experience metafield data
function getVisualExperiencePayload() {
  const el = document.getElementById('triunguloVisualExperience');
  if (!el || !el.textContent.trim()) return null;
  try {
    let raw = JSON.parse(el.textContent);
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch (e) {}
    }
    if (raw && typeof raw === 'object') {
      return raw.visual_experience || raw;
    }
  } catch (e) {}
  return null;
}

// Read any manual blob color overrides written by Liquid into data-blob-colors on #productHero
function readManualBlobColors() {
  const hero = document.getElementById('productHero');
  if (!hero) return null;
  const raw = hero.dataset.blobColors;
  if (!raw || raw.trim() === '') return null;
  // Same cleanup as the Liquid render: accept "#a,#b" as well as a JSON array string
  const parts = raw.replace(/[\[\]"']/g, '').split(',').map(s => s.trim()).filter(Boolean);
  const parsed = parts.map(parseColorToRgb).filter(Boolean);
  return parsed.length >= 1 ? parsed : null;
}

// True when the hero palette comes from custom.hero_blob_colors: those colors are used
// exactly as set (no teal, no generated colors, no saturation/lightness changes, no blending)
function isExactHeroPalette() {
  const hero = document.getElementById('productHero');
  return !!hero && hero.dataset.blobSource === 'hero_blob_colors';
}

// Read unified visual experience background blobs config strictly for .page-backdrop
function readBlobConfig() {
  const backdrop = document.querySelector('.page-backdrop');
  let config = null;

  // 1. Try reading from #triunguloVisualExperience script tag first (full JSON)
  const exp = getVisualExperiencePayload();
  if (exp && exp.background_blobs) {
    config = Object.assign({}, exp.background_blobs);
  }

  // 2. Try backdrop dataset blobConfig
  if (!config && backdrop && backdrop.dataset.blobConfig) {
    try {
      let parsed = JSON.parse(backdrop.dataset.blobConfig);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      config = (parsed && parsed.background_blobs) ? parsed.background_blobs : parsed;
    } catch (e) {}
  }

  // 3. Fallback to backdrop dataset backdropColors
  if (!config && backdrop && backdrop.dataset.backdropColors) {
    const parts = backdrop.dataset.backdropColors.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length >= 1) {
      config = { blob_colors: parts };
    }
  }

  if (!config) return null;

  // Normalize blob_colors to an array of colors or {r, g, b}
  if (config.blob_colors) {
    if (typeof config.blob_colors === 'string') {
      config.blob_colors = config.blob_colors.split(',').map(s => s.trim()).filter(Boolean);
    }
  }

  return config;
}

const TEAL_RGB = { r: 53, g: 202, b: 197 };  // Brand teal — always kept as guaranteed minimum base

function applyPalette(colors, isManual = false) {
  if (!colors || !colors.length) return;

  // Respect manual overrides from theme editor or product metafield
  const manualColors = isManual ? colors : readManualBlobColors();

  let base;
  if (manualColors && manualColors.length >= 1) {
    // Manual mode: always guarantee brand teal is in the palette as requested
    const hasTeal = manualColors.some(c => {
      const hsl = rgbToHsl(c);
      return hsl.h > 0.45 && hsl.h < 0.58 && hsl.s > 0.30;
    });
    base = hasTeal ? [...manualColors] : [...manualColors, TEAL_RGB];
  } else {
    // Auto mode: genuine extracted product colors + brand teal
    base = [...colors, TEAL_RGB];
  }

  // Pad to at least 6 entries by hue-rotating duplicates
  while (base.length < 6) {
    const src = base[base.length % base.length];
    const hsl = rgbToHsl(src);
    hsl.h = (hsl.h + 0.09) % 1;
    hsl.s = Math.min(0.98, hsl.s + 0.06);
    base.push(hslToRgb(hsl));
  }

  // Exact palette (custom.hero_blob_colors): the merchant's colors, repeated in order
  const exactHero = !!(manualColors && manualColors.length && isExactHeroPalette());

  // Hero colors: richly saturated, vivid spheres
  const heroColors = exactHero ? manualColors.map((c) => ({ ...c })) : base.map((c) => {
    const hsl = rgbToHsl(c);
    hsl.s = Math.min(1.0, Math.max(0.60, hsl.s * 1.50));
    hsl.l = Math.min(0.68, Math.max(0.35, hsl.l));
    return hslToRgb(hsl);
  });

  // Video colors: deep rich tones for dark video section
  const videoColors = base.map((c) => {
    const hsl = rgbToHsl(c);
    hsl.s = Math.min(1.0, Math.max(0.65, hsl.s * 1.45));
    hsl.l = Math.min(0.44, Math.max(0.20, hsl.l * 0.65));
    return hslToRgb(hsl);
  });

  // Soft page backdrop colors — if custom blob colors are provided in metafield, use them vibrantly
  const blobConfig = readBlobConfig();
  if (blobConfig && blobConfig.base_bg_color) {
    document.documentElement.style.setProperty('--page-bg', blobConfig.base_bg_color);
  }

  let pageColors = base.map((c) => {
    const hsl = rgbToHsl(c);
    hsl.s = Math.min(0.65, Math.max(0.30, hsl.s * 0.85));
    hsl.l = Math.min(0.88, Math.max(0.70, 0.78));
    return hslToRgb(hsl);
  });

  if (blobConfig && Array.isArray(blobConfig.blob_colors) && blobConfig.blob_colors.length >= 1) {
    const customList = blobConfig.blob_colors.map(parseColorToRgb).filter(Boolean);
    if (customList.length >= 1) {
      pageColors = customList;
    }
  }

  const paint = (selector, key, list, exact = false) => {
    const spans = document.querySelectorAll(selector);
    const spreadList = exact ? list : spread(list, Math.max(spans.length, 8));
    PALETTES[key] = spreadList.map((c) => rgbCss(c));
    spans.forEach((el, i) => {
      el.style.background = PALETTES[key][i % PALETTES[key].length];
    });
  };

  paint('.hero-glow span', 'hero', heroColors, exactHero);
  paint('.header-glow span', 'hero', heroColors, exactHero);
  paint('.video-glow span', 'video', videoColors);
  paint('.page-backdrop span', 'page', pageColors);

  // Contrast text calculation (hero section — glass panel over the vivid hero glow spheres)
  const lum = (c) => (.2126 * c.r + .7152 * c.g + .0722 * c.b) / 255;
  const fieldLum = heroColors.reduce((sum, c) => sum + lum(c), 0) / heroColors.length;
  const paleField = fieldLum > .72;
  document.documentElement.style.setProperty('--hero-ink', paleField ? '16 22 26' : '255 255 255');
  document.documentElement.style.setProperty('--hero-shadow',
    paleField ? 'rgba(255, 255, 255, .55)' : 'rgba(6, 28, 30, .32)');

  // Header contrast — driven by the hero blob colors directly under the header.
  // If the hero blobs are dark, all header elements automatically turn white.
  // If they are light/bright, header elements remain dark.
  const paleFieldHeader = fieldLum > .65;
  document.documentElement.classList.toggle('hero-palette-dark', !paleFieldHeader);
  document.documentElement.style.setProperty('--header-ink', paleFieldHeader ? '2 9 18' : '255 255 255');
  document.documentElement.style.setProperty('--header-logo-filter', paleFieldHeader ? 'none' : 'invert(1) brightness(1.6)');

  // Notify header custom element to immediately re-evaluate contrast
  document.querySelectorAll('[class*="ai-blur-header"]').forEach(h => {
    if (typeof h.updateContrastZone === 'function') {
      h.updateContrastZone();
    }
  });

  // Product accent from the genuine top color
  const primaryAccent = heroColors[0];
  const root = document.documentElement;
  root.style.setProperty('--product-accent', rgbCss(primaryAccent));
  root.style.setProperty('--product-accent-soft', rgbCss(primaryAccent, .18));
  heroColors.slice(0, 5).forEach((c, i) => root.style.setProperty(`--pc-${i + 1}`, rgbCss(c)));
}

function refreshPaletteFromElement(imgElement) {
  if (!imgElement) return;
  // If manual override is active, do not overwrite with auto-extracted colors
  if (readManualBlobColors()) return;

  const src = imgElement.currentSrc || imgElement.src || imgElement.dataset.src;
  if (src) {
    refreshPaletteFromSrc(src);
  } else {
    const run = () => {
      const colors = extractDominantColors(imgElement);
      if (colors && colors.length) {
        applyPalette(colors);
      }
    };
    if (imgElement.complete && imgElement.naturalWidth > 0) {
      run();
    } else {
      imgElement.addEventListener('load', run, { once: true });
    }
  }
}

function refreshPaletteFromSrc(src) {
  if (!src) return;
  // If manual override is active, do not overwrite with auto-extracted colors
  if (readManualBlobColors()) return;

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const colors = extractDominantColors(img);
    if (colors && colors.length) {
      applyPalette(colors);
    }
  };
  img.src = src;
}

/* ==========================================================================
   03. BLOB PARTICLE SIMULATION
   ========================================================================== */
function initBlobField() {
  const blobConfig = readBlobConfig();
  const small = window.matchMedia('(max-width: 780px)').matches;

  const pageCount = (blobConfig && blobConfig.blob_count) ? parseInt(blobConfig.blob_count, 10) : (small ? 6 : 9);
  const pageSpeed = (blobConfig && blobConfig.speed) ? parseInt(blobConfig.speed, 10) : 34;
  const pageSize = (blobConfig && blobConfig.blob_size_min && blobConfig.blob_size_max)
    ? [parseInt(blobConfig.blob_size_min, 10), parseInt(blobConfig.blob_size_max, 10)]
    : [30, 48];

  const SPEED = { hero: 82, video: 72, page: pageSpeed };
  const COUNT = small ? { hero: 8, video: 6, page: Math.max(4, Math.round(pageCount * 0.75)) } : { hero: 16, video: 9, page: pageCount };
  const SPARES = small ? { hero: 4, video: 3, page: 2 } : { hero: 8, video: 4, page: 3 };
  const POINTER_RADIUS = 240;
  const POINTER_FORCE = 420;

  const fields = [
    { key: 'hero',  glow: document.querySelector('.hero-glow'),     section: document.getElementById('productHero'),  size: [28, 44] },
    { key: 'video', glow: document.querySelector('.video-glow'),    section: document.getElementById('videoSection'), size: [24, 36] },
    { key: 'page',  glow: document.querySelector('.page-backdrop'), section: null, size: pageSize }
  ].filter((f) => f.glow);

  if (!fields.length) return;
  document.documentElement.classList.add('js-blobs');
  if (prefersReducedMotion()) return;

  const rand = (min, max) => min + Math.random() * (max - min);
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

  fields.forEach((field) => {
    field.speed = SPEED[field.key];
    field.maxSpeed = field.speed * 2.6;
    const template = field.glow.querySelector('span');
    if (!template) return;
    
    if (field.key === 'page' && blobConfig) {
      if (blobConfig.opacity) {
        field.baseOpacity = parseFloat(blobConfig.opacity);
        field.glow.style.setProperty('--blob-opacity', String(field.baseOpacity));
      }
      if (blobConfig.blur_amount) {
        field.glow.style.setProperty('--blob-blur', `${blobConfig.blur_amount}px`);
        template.style.filter = `blur(${blobConfig.blur_amount}px)`;
      }
    }
    if (!field.baseOpacity) {
      field.baseOpacity = parseFloat(getComputedStyle(template).opacity) || .8;
    }

    const total = COUNT[field.key] + SPARES[field.key];
    while (field.glow.querySelectorAll('span').length < total) {
      const clone = template.cloneNode(false);
      field.glow.appendChild(clone);
    }

    const pal = PALETTES[field.key];
    field.blobs = Array.from(field.glow.querySelectorAll('span')).map((el, i) => {
      const spare = i >= COUNT[field.key];
      el.style.transition = 'opacity 1.1s var(--ease-out), background-color 1.1s var(--ease-out)';
      el.style.opacity = spare ? '0' : String(field.baseOpacity);
      if (pal && pal.length && (!el.style.background || el.style.background === '')) {
        el.style.background = pal[i % pal.length];
      }
      if (field.key === 'page' && blobConfig && blobConfig.blur_amount) {
        el.style.filter = `blur(${blobConfig.blur_amount}px)`;
      }
      return { el, spare, active: !spare };
    });

    field.measure = () => {
      field.rect = field.glow.getBoundingClientRect();
      field.w = field.rect.width || window.innerWidth;
      field.h = field.rect.height || window.innerHeight;

      field.blobs.forEach((b, i) => {
        if (!b.seeded) {
          const cols = Math.ceil(Math.sqrt(field.blobs.length * 1.6));
          const rows = Math.ceil(field.blobs.length / cols);
          const size = rand(field.size[0], field.size[1]) * rem;
          b.el.style.width = b.el.style.height = `${size}px`;
          b.el.style.left = b.el.style.top = '0px';
          b.r = size / 2;
          b.x = ((i % cols) + .5) / cols * field.w + rand(-.12, .12) * field.w;
          b.y = (Math.floor(i / cols) + .5) / rows * field.h + rand(-.2, .2) * field.h;
          const angle = rand(0, Math.PI * 2);
          const speed = rand(.6, 1) * field.speed;
          b.vx = Math.cos(angle) * speed;
          b.vy = Math.sin(angle) * speed;
          b.squash = 0;
          b.seeded = true;
        }
        b.ax = b.r;
        b.ay = b.r;
      });
    };

    field.measure();
    field.visible = true;
    if (field.section && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => { field.visible = entry.isIntersecting; }, { rootMargin: '25% 0px' });
      io.observe(field.section);
    }
  });

  let pointer = null;
  window.addEventListener('pointermove', (e) => { pointer = { x: e.clientX, y: e.clientY }; }, { passive: true });
  window.addEventListener('pointerleave', () => { pointer = null; });

  const coverageAt = (field, x, y, skip) => {
    let best = 0;
    field.blobs.forEach((b) => {
      if (!b.active || b === skip) return;
      const d = Math.hypot(b.x - x, b.y - y);
      best = Math.max(best, 1 - d / (b.r * 1.15));
    });
    return best;
  };

  const refill = (field, px, py) => {
    const cols = 5, rows = 3;
    let worst = null;
    for (let gx = 0; gx < cols; gx++) {
      for (let gy = 0; gy < rows; gy++) {
        const x = (gx + .5) / cols * field.w;
        const y = (gy + .5) / rows * field.h;
        const cover = coverageAt(field, x, y);
        if (cover < .35 && (!worst || cover < worst.cover)) worst = { x, y, cover };
      }
    }

    if (worst) {
      const spare = field.blobs.find((b) => b.spare && !b.active);
      if (spare) {
        spare.x = worst.x;
        spare.y = worst.y;
        const angle = rand(0, Math.PI * 2);
        const speed = rand(.5, .9) * field.speed;
        spare.vx = Math.cos(angle) * speed;
        spare.vy = Math.sin(angle) * speed;
        const palette = PALETTES[field.key];
        if (palette && palette.length) {
          spare.el.style.background = palette[Math.floor(Math.random() * palette.length)];
        }
        spare.active = true;
        spare.el.style.opacity = String(field.baseOpacity);
      }
    }

    // Active infill under mouse cursor: if pointer is in hero, ensure the area directly
    // under and around the cursor is ALWAYS filled with color — never revealing white!
    if (px !== null && py !== null && field.key === 'hero') {
      const ptrCover = coverageAt(field, px, py);
      if (ptrCover < 0.65) {
        const spare = field.blobs.find((b) => b.spare && !b.active);
        if (spare) {
          spare.x = px + rand(-45, 45);
          spare.y = py + rand(-45, 45);
          const angle = rand(0, Math.PI * 2);
          const speed = rand(.35, .7) * field.speed;
          spare.vx = Math.cos(angle) * speed;
          spare.vy = Math.sin(angle) * speed;
          const palette = PALETTES[field.key];
          if (palette && palette.length) {
            spare.el.style.background = palette[Math.floor(Math.random() * palette.length)];
          }
          spare.active = true;
          spare.el.style.opacity = String(field.baseOpacity);
        }
      }
    }

    field.blobs.forEach((b) => {
      if (!b.spare || !b.active) return;
      if (coverageAt(field, b.x, b.y, b) > .65) {
        b.active = false;
        b.el.style.opacity = '0';
      }
    });
  };

  const step = (field, dt) => {
    const { blobs, w, h } = field;
    const padX = w * .35;
    const padY = h * .45;

    let px = null, py = null;
    if (pointer && field.rect) {
      px = pointer.x - field.rect.left;
      py = pointer.y - field.rect.top;
    }

    blobs.forEach((b) => {
      if (!b.active) return;
      if (px !== null) {
        const dx = b.x - px, dy = b.y - py;
        const dist = Math.hypot(dx, dy) || .001;
        if (dist < POINTER_RADIUS) {
          const falloff = 1 - dist / POINTER_RADIUS;
          const push = POINTER_FORCE * falloff * falloff * dt;
          // Fluid tangential vortex vectors
          const tx = -dy / dist;
          const ty = dx / dist;
          // Soft displacement combined with fluid vortex swirl
          b.vx += (dx / dist) * (push * 0.35) + tx * (push * 0.85);
          b.vy += (dy / dist) * (push * 0.35) + ty * (push * 0.85);
        }
      }

      b.vx *= .995;
      b.vy *= .995;

      const sp = Math.hypot(b.vx, b.vy);
      if (sp > field.maxSpeed) { b.vx *= field.maxSpeed / sp; b.vy *= field.maxSpeed / sp; }
      else if (sp < field.speed * .45) {
        const k = (field.speed * .45) / (sp || .001);
        b.vx *= k; b.vy *= k;
      }

      b.x += b.vx * dt;
      b.y += b.vy * dt;

      if (b.x < -padX) { b.x = -padX; b.vx = Math.abs(b.vx); }
      if (b.x > w + padX) { b.x = w + padX; b.vx = -Math.abs(b.vx); }
      if (b.y < -padY) { b.y = -padY; b.vy = Math.abs(b.vy); }
      if (b.y > h + padY) { b.y = h + padY; b.vy = -Math.abs(b.vy); }

      if (b.squash > 0) b.squash = Math.max(0, b.squash - dt * 2.2);
    });

    // Dense liquid sea: blobs squeeze against each other and transfer momentum
    const live = blobs.filter((b) => b.active);
    for (let i = 0; i < live.length; i++) {
      for (let j = i + 1; j < live.length; j++) {
        const a = live[i], c = live[j];
        const dx = c.x - a.x, dy = c.y - a.y;
        const dist = Math.hypot(dx, dy) || .001;
        const min = (a.r + c.r) * .62;
        if (dist >= min) continue;

        const nx = dx / dist, ny = dy / dist;
        const push = (min - dist) * 0.52;
        a.x -= nx * push; a.y -= ny * push;
        c.x += nx * push; c.y += ny * push;

        const vn = (c.vx - a.vx) * nx + (c.vy - a.vy) * ny;
        if (vn < 0) {
          const impulse = vn * .75;
          a.vx += impulse * nx; a.vy += impulse * ny;
          c.vx -= impulse * nx; c.vy -= impulse * ny;
          a.squash = c.squash = 1;
        }
      }
    }

    blobs.forEach((b) => {
      if (!b.active && b.el.style.opacity === '0' && !b.wasActive) return;
      b.wasActive = b.active;
      const scale = 1 - b.squash * .07;
      b.el.style.transform = `translate3d(${(b.x - b.ax).toFixed(1)}px, ${(b.y - b.ay).toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
    });
  };

  let last = performance.now();
  let sinceRect = 0, sinceRefill = 0;
  const frame = (now) => {
    const dt = Math.min(.05, (now - last) / 1000);
    last = now;
    sinceRect += dt;
    sinceRefill += dt;

    if (sinceRect > .25) {
      sinceRect = 0;
      fields.forEach((f) => { f.rect = f.glow.getBoundingClientRect(); });
    }
    if (!document.hidden) {
      fields.forEach((field) => { if (field.visible) step(field, dt); });
      if (sinceRefill > .2) {
        sinceRefill = 0;
        fields.forEach((field) => {
          if (field.visible) {
            let px = null, py = null;
            if (pointer && field.rect) {
              px = pointer.x - field.rect.left;
              py = pointer.y - field.rect.top;
            }
            refill(field, px, py);
          }
        });
      }
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => fields.forEach((f) => f.measure()), 200);
  });
}

/* ==========================================================================
   04. CURSOR SPECULAR HIGHLIGHT
   ========================================================================== */
function initGlassPointer() {
  if (window.matchMedia('(hover: none)').matches) return;
  const selector = '.glass, .glass-panel, .glass-dark, .glass-chip, .btn-cta, .btn-soft, ' +
                   '.variation-box, .gallery-main, .gallery-thumb, .video-frame, .manual-glass-btn';

  const track = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
  };

  const attach = (el) => {
    if (el.dataset.glassBound) return;
    el.dataset.glassBound = '1';
    el.addEventListener('pointermove', track);
  };

  document.querySelectorAll(selector).forEach(attach);
}

/* ==========================================================================
   05. GALLERY & SYNCHRONIZED HERO
   ========================================================================== */
function initGallery() {
  const main = document.getElementById('galleryMainImage');
  const thumbs = Array.from(document.querySelectorAll('.gallery-thumb'));
  const counter = document.getElementById('galleryCounter');
  const heroImg = document.getElementById('heroProduct');

  if (!main || !thumbs.length) return;

  const select = (index) => {
    const btn = thumbs[index];
    if (!btn) return;

    thumbs.forEach((t, i) => t.classList.toggle('active', i === index));
    if (counter) counter.textContent = `${index + 1} / ${thumbs.length}`;

    const newSrc = btn.dataset.fullSrc || btn.querySelector('img')?.src;
    if (!newSrc) return;

    const swap = () => {
      main.src = newSrc;
      main.alt = btn.querySelector('img')?.alt || '';
      main.style.opacity = '1';
      if (!readManualBlobColors()) {
        refreshPaletteFromSrc(newSrc);
      }
    };

    if (prefersReducedMotion()) {
      swap();
    } else {
      main.style.opacity = '0';
      setTimeout(swap, 160);
    }

    if (heroImg) {
      heroImg.src = newSrc;
      heroImg.classList.add('swapping');
      setTimeout(() => heroImg.classList.remove('swapping'), 520);
    }
  };

  thumbs.forEach((thumb, idx) => {
    thumb.addEventListener('click', () => select(idx));
  });

  window.selectGalleryImageBySrc = (src) => {
    if (!src) return;
    const idx = thumbs.findIndex((t) => {
      const s = t.dataset.fullSrc || t.querySelector('img')?.src || '';
      return s.includes(src) || src.includes(s);
    });
    if (idx !== -1) select(idx);
  };
}

/* ==========================================================================
   06. SHOPIFY VARIANTS & PREORDER ENGINE
   ========================================================================== */
function initVariants() {
  const variantDataEl = document.getElementById('ProductVariantsJson');
  if (!variantDataEl) return;

  let variants = [];
  try {
    variants = JSON.parse(variantDataEl.textContent);
  } catch (e) {
    console.error('Failed to parse product variants JSON', e);
    return;
  }

  const optionGroups = Array.from(document.querySelectorAll('.variation-options'));
  const hiddenSelect = document.getElementById('ProductSelect');
  const priceEl = document.getElementById('productPrice');
  const comparePriceEl = document.getElementById('productComparePrice');
  const buyBtn = document.getElementById('addToCartBtn');
  const preorderBanner = document.getElementById('preorderBanner');
  const outOfStockBanner = document.getElementById('outOfStockBanner');

  const getSelectedOptions = () => {
    const selected = [];
    optionGroups.forEach((group) => {
      const activeBox = group.querySelector('.variation-box.selected');
      if (activeBox) selected.push(activeBox.dataset.value);
    });
    return selected;
  };

  const updateVariant = () => {
    const selected = getSelectedOptions();
    const currentVariant = variants.find((v) => {
      return v.options.every((opt, idx) => opt === selected[idx]);
    }) || variants[0];

    if (!currentVariant) return;

    // Update hidden select
    if (hiddenSelect) hiddenSelect.value = currentVariant.id;

    // Update URL parameter
    if (window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', currentVariant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Update Price
    if (priceEl && currentVariant.price_formatted) {
      priceEl.textContent = currentVariant.price_formatted;
    }
    if (comparePriceEl) {
      if (currentVariant.compare_at_price > currentVariant.price) {
        comparePriceEl.textContent = currentVariant.compare_at_price_formatted;
        comparePriceEl.hidden = false;
      } else {
        comparePriceEl.hidden = true;
      }
    }

    // Update Gallery Image if variant has an image
    if (currentVariant.featured_image && currentVariant.featured_image.src) {
      if (window.selectGalleryImageBySrc) {
        window.selectGalleryImageBySrc(currentVariant.featured_image.src);
      }
    }

    // Determine Preorder & Stock Status
    // Standard Shopify preorder: inventory_policy == 'continue' && inventory_quantity <= 0
    // Or tagged preorder / metafield
    const isPreorder = currentVariant.is_preorder ||
      (currentVariant.inventory_policy === 'continue' && currentVariant.inventory_quantity <= 0) ||
      document.body.classList.contains('product-is-preorder');

    const isAvailable = currentVariant.available;

    if (!isAvailable) {
      // Sold out
      if (outOfStockBanner) outOfStockBanner.hidden = false;
      if (preorderBanner) preorderBanner.hidden = true;
      if (buyBtn) {
        buyBtn.disabled = true;
        const lbl = buyBtn.querySelector('.btn-label');
        if (lbl) lbl.textContent = 'Out of stock';
      }
    } else if (isPreorder) {
      // Preorder active
      if (outOfStockBanner) outOfStockBanner.hidden = true;
      if (preorderBanner) preorderBanner.hidden = false;
      if (buyBtn) {
        buyBtn.disabled = false;
        const lbl = buyBtn.querySelector('.btn-label');
        if (lbl) lbl.textContent = buyBtn.dataset.preorderText || 'Preorder now';
      }
    } else {
      // Normal In Stock
      if (outOfStockBanner) outOfStockBanner.hidden = true;
      if (preorderBanner) preorderBanner.hidden = true;
      if (buyBtn) {
        buyBtn.disabled = false;
        const lbl = buyBtn.querySelector('.btn-label');
        if (lbl) lbl.textContent = buyBtn.dataset.defaultText || 'Add to cart';
      }
    }
  };

  optionGroups.forEach((group) => {
    const boxes = group.querySelectorAll('.variation-box');
    boxes.forEach((box) => {
      box.addEventListener('click', () => {
        boxes.forEach((b) => b.classList.remove('selected'));
        box.classList.add('selected');
        updateVariant();
      });
    });
  });

  // Initial update
  updateVariant();
}

/* ==========================================================================
   07. QUANTITY STEPPER
   ========================================================================== */
function initQuantity() {
  const input = document.getElementById('qtyInput');
  const minus = document.getElementById('qtyMinus');
  const plus = document.getElementById('qtyPlus');
  if (!input) return;

  const min = parseInt(input.min, 10) || 1;
  const max = parseInt(input.max, 10) || 99;

  const getQty = () => {
    const n = parseInt(input.value, 10);
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : min;
  };

  const sync = () => {
    const val = getQty();
    input.value = String(val);
    if (minus) minus.disabled = val <= min;
    if (plus) plus.disabled = val >= max;
  };

  const step = (delta) => { input.value = String(getQty() + delta); sync(); };

  minus && minus.addEventListener('click', () => step(-1));
  plus && plus.addEventListener('click', () => step(1));
  input.addEventListener('change', sync);
  input.addEventListener('blur', sync);
  sync();
}

/* ==========================================================================
   08. SHOPIFY AJAX ADD TO CART
   ========================================================================== */
function initAddToCart() {
  const btn = document.getElementById('addToCartBtn');
  const form = document.getElementById('productForm');
  if (!btn) return;

  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    if (btn.disabled) return;

    const label = btn.querySelector('.btn-label');
    const originalText = label ? label.textContent : 'Add to cart';
    const variantId = document.getElementById('ProductSelect')?.value;
    const qty = parseInt(document.getElementById('qtyInput')?.value, 10) || 1;

    if (!variantId) return;

    btn.disabled = true;
    if (label) label.textContent = 'Adding…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });

      if (response.ok) {
        btn.classList.add('is-added');
        if (label) label.textContent = qty > 1 ? `Added ${qty} to cart ✓` : 'Added to cart ✓';

        // Notify Shopify cart drawers or counters
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { variantId, quantity: qty } }));
        document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));

        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.disabled = false;
          if (label) label.textContent = originalText;
        }, 1500);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (err) {
      // In local preview/test mode without Shopify backend, simulate success
      btn.classList.add('is-added');
      if (label) label.textContent = qty > 1 ? `Added ${qty} to cart ✓` : 'Added to cart ✓';
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.disabled = false;
        if (label) label.textContent = originalText;
      }, 1500);
    }
  });
}

/* ==========================================================================
   09. HOTSPOTS & MOBILE SHEET
   ========================================================================== */
function initHotspots() {
  const container = document.getElementById('hotspotContainer');
  const container2 = document.getElementById('hotspotContainer2');
  const hotspotSection = document.querySelector('.hotspot-section');
  const frame = document.getElementById('hotspotFrame');
  const frame2 = document.getElementById('hotspotFrame2');
  const gridLayout = document.getElementById('hotspotGridLayout') || document.querySelector('.hotspot-grid-layout');
  const exp = (typeof getVisualExperiencePayload === 'function') ? getVisualExperiencePayload() : null;

  if (exp && exp.hotspots) {
    const h = exp.hotspots;
    if (h.enabled === false) {
      if (hotspotSection) {
        const wrap = hotspotSection.querySelector('.hotspot-manual-wrap');
        const hasManual = wrap && wrap.style.display !== 'none';
        if (!hasManual) {
          hotspotSection.style.display = 'none';
        } else {
          const marquee = hotspotSection.querySelector('.marquee-band');
          if (marquee) marquee.style.display = 'none';
          if (frame) frame.style.display = 'none';
          if (frame2) frame2.style.display = 'none';
        }
      }
      return;
    }

    // Dual Hotspots Mode toggle (strictly requires layout === 'dual' or dual_mode === true)
    const isDual = Boolean(
      (h.layout === 'dual' || h.dual_mode === true || h.dual_images === true) &&
      h.layout !== 'single' &&
      h.dual_mode !== false &&
      h.image_2_url &&
      String(h.image_2_url).trim() !== ''
    );
    if (gridLayout) {
      gridLayout.classList.toggle('is-dual', isDual);
    }
    if (frame2) {
      frame2.style.display = isDual ? 'flex' : 'none';
    }

    // Card 1 Base Image
    if (h.image_url && container) {
      const img = container.querySelector('img.hotspot-image');
      if (img && img.src !== h.image_url) {
        img.src = h.image_url;
      }
    }

    // Card 2 Base Image (Dual Mode)
    if (isDual && h.image_2_url && container2) {
      let img2 = container2.querySelector('img.hotspot-image');
      if (!img2) {
        img2 = document.createElement('img');
        img2.className = 'hotspot-image';
        img2.crossOrigin = 'anonymous';
        img2.loading = 'lazy';
        container2.insertBefore(img2, container2.firstChild);
      }
      if (img2 && img2.src !== h.image_2_url) {
        img2.src = h.image_2_url;
      }
    }

    // Glass Card Frame Tints
    if (h.card_tint) {
      if (frame) frame.setAttribute('data-tint-colors', h.card_tint);
      if (frame2) frame2.setAttribute('data-tint-colors', h.card_tint);
      if (typeof initImageCardTints === 'function') {
        initImageCardTints();
      }
    }

    // Infinite Marquee Ticker Text
    if (h.marquee_text && hotspotSection) {
      hotspotSection.querySelectorAll('.marquee__content li:not(.marquee-sep)').forEach(li => {
        li.textContent = h.marquee_text;
      });
    }

    // Card 1 Pins
    const items = h.items || (Array.isArray(h) ? h : null);
    if (Array.isArray(items) && items.length > 0 && container) {
      container.querySelectorAll('.t_hotspot').forEach(el => el.remove());
      items.forEach(spot => {
        const btn = document.createElement('button');
        btn.className = 't_hotspot';
        btn.style.left = `${spot.x}%`;
        btn.style.top = `${spot.y}%`;
        btn.setAttribute('data-title', spot.title || '');
        btn.setAttribute('data-txt', spot.text || '');
        btn.setAttribute('aria-label', spot.title || 'Product hotspot');
        btn.innerHTML = `<svg class="ico ico-sm"><use href="#i-plus"/></svg>`;
        container.appendChild(btn);
      });
    }

    // Card 2 Pins (Dual Mode)
    if (isDual && container2) {
      const items2 = h.image_2_items || [];
      if (Array.isArray(items2) && items2.length > 0) {
        container2.querySelectorAll('.t_hotspot').forEach(el => el.remove());
        items2.forEach(spot => {
          const btn = document.createElement('button');
          btn.className = 't_hotspot';
          btn.style.left = `${spot.x}%`;
          btn.style.top = `${spot.y}%`;
          btn.setAttribute('data-title', spot.title || '');
          btn.setAttribute('data-txt', spot.text || '');
          btn.setAttribute('aria-label', spot.title || 'Product hotspot');
          btn.innerHTML = `<svg class="ico ico-sm"><use href="#i-plus"/></svg>`;
          container2.appendChild(btn);
        });
      }
    }
  }

  const dots = Array.from(document.querySelectorAll('.t_hotspot'));
  const sheet = document.getElementById('hotspotSheet');
  const sheetBackdrop = document.getElementById('hotspotSheetBackdrop');
  const sheetTitle = document.getElementById('hotspotSheetTitle');
  const sheetText = document.getElementById('hotspotSheetText');
  const sheetClose = document.getElementById('hotspotSheetClose');
  if (!dots.length) return;

  const isDesktop = () => window.matchMedia('(min-width: 781px)').matches;

  const hideTooltip = () => {
    document.querySelectorAll('.hotspot-tooltip').forEach(tt => tt.classList.remove('visible'));
    dots.forEach((d) => d.classList.remove('active'));
  };

  const showTooltip = (dot) => {
    const parentContainer = dot.closest('.hotspot-container') || dot.offsetParent || dot.parentElement;
    let tooltip = parentContainer ? parentContainer.querySelector('.hotspot-tooltip') : null;
    if (!tooltip) tooltip = document.getElementById('hotspotTooltip');
    if (!tooltip) return;

    const title = dot.dataset.title || '';
    const txt = dot.dataset.txt || '';
    tooltip.innerHTML = `${title ? `<h5>${title}</h5>` : ''}${txt ? `<p>${txt}</p>` : ''}`;

    const cw = parentContainer.clientWidth;
    const ch = parentContainer.clientHeight;

    tooltip.style.visibility = 'hidden';
    tooltip.classList.add('visible');
    const tw = tooltip.offsetWidth;
    const th = tooltip.offsetHeight;

    const dotX = dot.offsetLeft;
    const dotY = dot.offsetTop;
    const dotR = dot.offsetWidth / 2;

    let left = dotX - tw / 2;
    left = Math.max(12, Math.min(left, cw - tw - 12));

    const above = dotY - dotR - th - 14;
    const top = above > 8 ? above : dotY + dotR + 14;

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${Math.min(top, ch - th - 12)}px`;
    tooltip.style.visibility = 'visible';

    dots.forEach((d) => d.classList.remove('active'));
    dot.classList.add('active');
  };

  const preventScroll = (e) => {
    if (sheet && sheet.contains(e.target)) return;
    e.preventDefault();
  };

  const openSheet = (dot) => {
    if (!sheet) return;
    if (dot && typeof dot.blur === 'function') dot.blur();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    dots.forEach((d) => d.classList.remove('active'));
    if (dot) dot.classList.add('active');
    const frame = dot ? (dot.closest('.hotspot-frame') || dot.closest('.hotspot-container')) : null;
    if (frame) frame.classList.add('sheet-active');

    sheetTitle.textContent = dot.dataset.title || '';
    sheetText.textContent = dot.dataset.txt || '';
    sheet.classList.add('open');
    sheetBackdrop && sheetBackdrop.classList.add('open');
    document.documentElement.classList.add('no-scroll');
    document.addEventListener('touchmove', preventScroll, { passive: false });
  };

  const closeSheet = () => {
    if (!sheet) return;
    sheet.classList.remove('open');
    sheetBackdrop && sheetBackdrop.classList.remove('open');
    document.documentElement.classList.remove('no-scroll');
    document.removeEventListener('touchmove', preventScroll);
    dots.forEach((d) => d.classList.remove('active'));
    document.querySelectorAll('.sheet-active').forEach((el) => el.classList.remove('sheet-active'));
  };

  dots.forEach((dot) => {
    dot.addEventListener('mouseenter', () => { if (isDesktop()) showTooltip(dot); });
    dot.addEventListener('mouseleave', () => { if (isDesktop()) hideTooltip(); });
    dot.addEventListener('focus', () => { if (isDesktop()) showTooltip(dot); });
    dot.addEventListener('blur', hideTooltip);
    dot.addEventListener('click', (e) => {
      e.preventDefault();
      if (dot && typeof dot.blur === 'function') dot.blur();
      isDesktop() ? showTooltip(dot) : openSheet(dot);
    });
  });

  sheetClose && sheetClose.addEventListener('click', closeSheet);
  sheetBackdrop && sheetBackdrop.addEventListener('click', closeSheet);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeSheet(); hideTooltip(); }
  });
  window.addEventListener('scroll', hideTooltip, { passive: true });
}

/* ==========================================================================
   10. CINEMATIC MULTI-VIDEO SWITCHER
   ========================================================================== */
function initVideoSwitcher() {
  const tabs = Array.from(document.querySelectorAll('.video-tab-btn'));
  const iframe = document.getElementById('productVideoIframe');
  if (!tabs.length || !iframe) return;

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const url = tab.dataset.videoUrl;
      if (!url) return;

      tabs.forEach((t) => t.classList.remove('active'));
      tab.classList.add('active');

      iframe.style.opacity = '0';
      setTimeout(() => {
        iframe.src = url;
        iframe.style.opacity = '1';
      }, 200);
    });
  });
}

/* ==========================================================================
   11. RELATED PRODUCTS CAROUSEL
   ========================================================================== */
function initSuggestionCarousel() {
  const lists = document.querySelectorAll('.horizontal-list, [id^="suggestionList"]');
  if (!lists.length) return;

  lists.forEach((list) => {
    const section = list.closest('.suggestion-section') || list.parentElement;
    const leftBtn = (section && section.querySelector('.suggestion-arrow.left')) || document.querySelector('[id^="scrollLeftBtn"]');
    const rightBtn = (section && section.querySelector('.suggestion-arrow.right')) || document.querySelector('[id^="scrollRightBtn"]');

    const update = () => {
      const atStart = list.scrollLeft <= 8;
      const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 8;
      if (leftBtn) leftBtn.classList.toggle('hide', atStart);
      if (rightBtn) rightBtn.classList.toggle('hide', atEnd);
      list.style.setProperty('--fade-l', atStart ? '0px' : '3.5rem');
      list.style.setProperty('--fade-r', atEnd ? '0px' : '3.5rem');
    };

    const step = () => Math.max(260, Math.round(list.clientWidth * 0.75));

    if (leftBtn && !leftBtn._hasListener) {
      leftBtn.addEventListener('click', (e) => {
        e.preventDefault();
        list.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      leftBtn._hasListener = true;
    }
    if (rightBtn && !rightBtn._hasListener) {
      rightBtn.addEventListener('click', (e) => {
        e.preventDefault();
        list.scrollBy({ left: step(), behavior: 'smooth' });
      });
      rightBtn._hasListener = true;
    }
    list.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });

  balanceCardDescriptions();
  initRecommendationCardTints();
}

window.initSuggestionCarousel = initSuggestionCarousel;
window.initRecommendationCardTints = initRecommendationCardTints;

/* ==========================================================================
   12. SCROLL REVEALS
   ========================================================================== */
function initScrollReveal() {
  const items = document.querySelectorAll('.reveal');
  if (!items.length) return;

  if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
    items.forEach((el) => el.classList.add('in'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: .15, rootMargin: '0px 0px -8% 0px' });

  items.forEach((el) => observer.observe(el));
}

function initDescriptionReveal() {
  const figure = document.getElementById('descriptionImage');
  if (!figure) return;

  if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
    figure.classList.add('visible');
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        figure.classList.add('visible');
        observer.disconnect();
      }
    });
  }, { threshold: .08, rootMargin: '0px 0px -20px 0px' });

  observer.observe(figure);
}

/* ==========================================================================
   13. CHROMATIC GLASS CARD TINTING (HOTSPOTS & DESCRIPTION)
   Extracts 2-3 genuine dominant hues from the section image (or reads
   Theme Editor overrides) and applies a delicate chromatic glass tint.
   ========================================================================== */
function initImageCardTints() {
  // 1. Hotspots Section (supports single and dual frames)
  const hotspotFrames = document.querySelectorAll('.hotspot-frame');
  hotspotFrames.forEach(frame => {
    const img = frame.querySelector('.hotspot-image');
    applyCardTint(frame, img);
  });

  // 2. Product Description Section
  const descFigure = document.getElementById('descriptionImage') || document.querySelector('.description-figure');
  const descImg = descFigure ? descFigure.querySelector('img') : null;
  if (descFigure) {
    applyCardTint(descFigure, descImg);
  }
}

function applyCardTint(cardElement, imageElement) {
  if (!cardElement) return;

  // Check Theme Editor manual override via data-tint-colors
  const manualTint = cardElement.getAttribute('data-tint-colors');
  if (manualTint) {
    const parsed = manualTint.split(',').map(s => parseColorToRgb(s.trim())).filter(Boolean);
    if (parsed.length > 0) {
      setCardTintStyles(cardElement, parsed);
      return;
    }
  }

  // Check visual experience payload tint override if manual data attribute not set
  if (cardElement.id === 'descriptionImage' && typeof getVisualExperiencePayload === 'function') {
    const exp = getVisualExperiencePayload();
    if (exp && exp.description_card && exp.description_card.tint) {
      const tintRgb = parseColorToRgb(exp.description_card.tint);
      if (tintRgb) {
        setCardTintStyles(cardElement, [tintRgb]);
        return;
      }
    }
  }

  if (cardElement.classList.contains('hotspot-frame') && typeof getVisualExperiencePayload === 'function') {
    const exp = getVisualExperiencePayload();
    if (exp && exp.hotspots && exp.hotspots.card_tint) {
      const tintRgb = parseColorToRgb(exp.hotspots.card_tint);
      if (tintRgb) {
        setCardTintStyles(cardElement, [tintRgb]);
        return;
      }
    }
  }

  if (!imageElement) return;

  const extractAndApply = () => {
    try {
      const src = imageElement.currentSrc || imageElement.src;
      if (!src) return;

      const imgObj = new Image();
      imgObj.crossOrigin = 'anonymous';
      imgObj.onload = () => {
        try {
          const colors = extractDominantColors(imgObj, 3);
          if (colors && colors.length > 0) {
            setCardTintStyles(cardElement, colors);
          }
        } catch (e) {
          console.warn('Card tint extraction warning:', e);
        }
      };
      imgObj.src = src;
    } catch (err) {
      console.warn('Card tint setup warning:', err);
    }
  };

  if (imageElement.complete && imageElement.naturalWidth > 0) {
    extractAndApply();
  } else {
    imageElement.addEventListener('load', extractAndApply, { once: true });
  }
}

function setCardTintStyles(cardElement, colors) {
  if (!cardElement || !colors || !colors.length) return;
  const c1 = colors[0];
  const c2 = colors[1] || colors[0];
  const c3 = colors[2] || colors[1] || colors[0];

  cardElement.style.setProperty('--tint-c1', `${c1.r}, ${c1.g}, ${c1.b}`);
  cardElement.style.setProperty('--tint-c2', `${c2.r}, ${c2.g}, ${c2.b}`);
  cardElement.style.setProperty('--tint-c3', `${c3.r}, ${c3.g}, ${c3.b}`);
  cardElement.classList.add('has-tint');
}

/* ==========================================================================
   14. BALANCED CARD DESCRIPTION SPLIT
   Ensures recommendation short descriptions split across lines evenly
   without awkward short or dangling words.
   ========================================================================== */
function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function balanceCardDescriptions() {
  const items = document.querySelectorAll('.card-type, .card-meta-desc');
  items.forEach(el => {
    const raw = (el.getAttribute('data-raw-text') || el.textContent).trim().replace(/\s+/g, ' ');
    if (!raw) return;
    el.setAttribute('data-raw-text', raw);
    const words = raw.split(' ');
    if (words.length <= 2) {
      el.textContent = raw;
      return;
    }

    if (raw.length > 16) {
      let bestIdx = 1;
      let minDiff = Infinity;
      for (let i = 1; i < words.length; i++) {
        const l1 = words.slice(0, i).join(' ').length;
        const l2 = words.slice(i).join(' ').length;
        const diff = Math.abs(l1 - l2);
        if (diff < minDiff) {
          minDiff = diff;
          bestIdx = i;
        }
      }
      const line1 = words.slice(0, bestIdx).join(' ');
      const line2 = words.slice(bestIdx).join(' ');
      el.innerHTML = `<span class="balanced-line">${escapeHtml(line1)}</span><span class="balanced-line">${escapeHtml(line2)}</span>`;
    }
  });
}

/* ==========================================================================
   15. MOBILE PRODUCT DESCRIPTION MEDIA POSITIONING
   On mobile (<= 899px), places the glass detail figure either:
   - 'top': directly after heading (title) and before description body
   - 'bottom': at the bottom of the description section (after all text & features),
               immediately before the Cinematic Image section.
   Restores original 2-column layout on desktop (> 899px).
   ========================================================================== */
function initDescriptionMediaMobile() {
  const grid = document.querySelector('.description-grid');
  const media = document.querySelector('.description-media');
  const textContainer = document.querySelector('.description-text');
  const heading = textContainer ? textContainer.querySelector('h2') : null;
  const body = document.querySelector('.description-body');
  if (!grid || !media) return;

  let anchor = document.getElementById('descMediaGridAnchor');
  if (!anchor) {
    anchor = document.createElement('span');
    anchor.id = 'descMediaGridAnchor';
    anchor.style.display = 'none';
    grid.appendChild(anchor);
  }

  const getMobilePos = () => {
    try {
      if (typeof getVisualExperiencePayload === 'function') {
        const exp = getVisualExperiencePayload();
        if (exp && exp.description_card && exp.description_card.mobile_position) {
          return String(exp.description_card.mobile_position).toLowerCase().trim();
        }
      }
    } catch (e) {}

    if (grid.dataset && grid.dataset.mobilePos) {
      return grid.dataset.mobilePos.toLowerCase().trim();
    }
    const section = document.getElementById('descriptionSection') || document.querySelector('.description-section');
    if (section && section.dataset && section.dataset.mobilePos) {
      return section.dataset.mobilePos.toLowerCase().trim();
    }
    if (grid.classList.contains('mobile-pos-bottom')) return 'bottom';
    return 'top';
  };

  const updatePosition = () => {
    const isMobile = window.innerWidth <= 899;
    const pos = getMobilePos();

    grid.classList.toggle('mobile-pos-bottom', pos === 'bottom');
    grid.classList.toggle('mobile-pos-top', pos !== 'bottom');

    if (isMobile) {
      if (pos === 'bottom') {
        // Place at the bottom of description section: after description-text & feature list,
        // right before Cinematic Image section!
        if (textContainer) {
          if (textContainer.nextSibling !== media) {
            textContainer.after(media);
          }
        } else if (media.parentElement !== grid) {
          grid.appendChild(media);
        }
      } else {
        // Place at the top of description: right after heading and before body
        if (heading) {
          if (heading.nextSibling !== media) {
            heading.after(media);
          }
        } else if (body) {
          if (body.previousSibling !== media) {
            body.before(media);
          }
        } else if (textContainer) {
          if (textContainer.previousSibling !== media) {
            textContainer.before(media);
          }
        }
      }
    } else {
      // Desktop: Restore to original location in description-grid for side-by-side 2-column layout
      if (media.parentElement !== grid || media.nextSibling !== anchor) {
        grid.insertBefore(media, anchor);
      }
    }
  };

  updatePosition();
  window.addEventListener('resize', updatePosition);
}

/* ==========================================================================
   16. SHOPIFY RICH TEXT AST METACONVERTER
   Detects AST strings like {"type"=>"root", "children"=>...} and transforms
   them into clean, formatted HTML preserving bold, italic, and paragraphs.
   ========================================================================== */
function cleanRichTextMetafields() {
  const targets = document.querySelectorAll('.shop-description, .card-meta-desc');
  targets.forEach(el => {
    const content = el.innerHTML || '';
    if (content.includes('type=&gt;&quot;root&quot;') || content.includes('type=>"root"') || content.includes('"type":"root"')) {
      const decoded = content
        .replace(/&quot;/g, '"')
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        .replace(/&amp;/g, '&');
      const rendered = parseShopifyRichTextAst(decoded);
      if (rendered) {
        if (el.classList.contains('card-meta-desc')) {
          const tmp = document.createElement('div');
          tmp.innerHTML = rendered;
          el.textContent = tmp.textContent || tmp.innerText || '';
        } else {
          el.innerHTML = rendered;
        }
      }
    }
  });
}

function parseShopifyRichTextAst(str) {
  if (!str || typeof str !== 'string') return '';
  try {
    const jsonStr = str
      .replace(/=>/g, ':')
      .replace(/:([a-zA-Z0-9_]+)/g, '"$1"');
    const ast = JSON.parse(jsonStr);
    return renderAstNodes(ast.children || [ast]);
  } catch (e) {
    return renderAstFallback(str);
  }
}

function renderAstNodes(nodes) {
  if (!Array.isArray(nodes)) return '';
  return nodes.map(node => {
    if (!node) return '';
    if (node.type === 'text') {
      let val = escapeHtml(node.value || '');
      if (node.bold) val = `<strong>${val}</strong>`;
      if (node.italic) val = `<em>${val}</em>`;
      if (node.underline) val = `<u>${val}</u>`;
      return val;
    }
    if (node.type === 'link') {
      const inner = renderAstNodes(node.children || []);
      return `<a href="${escapeHtml(node.url || '#')}" target="_blank" rel="noopener">${inner}</a>`;
    }
    if (node.type === 'paragraph') {
      const inner = renderAstNodes(node.children || []);
      return `<p>${inner}</p>`;
    }
    if (node.type === 'heading') {
      const level = node.level || 3;
      const inner = renderAstNodes(node.children || []);
      return `<h${level}>${inner}</h${level}>`;
    }
    if (node.type === 'list') {
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      const inner = renderAstNodes(node.children || []);
      return `<${tag}>${inner}</${tag}>`;
    }
    if (node.type === 'list-item') {
      const inner = renderAstNodes(node.children || []);
      return `<li>${inner}</li>`;
    }
    if (node.children) {
      return renderAstNodes(node.children);
    }
    return node.value ? escapeHtml(node.value) : '';
  }).join('');
}

function renderAstFallback(str) {
  const matches = [];
  const regex = /"value"\s*(?:=>|:)\s*"([^"\\]*(?:\\.[^"\\]*)*)"/g;
  let match;
  while ((match = regex.exec(str)) !== null) {
    let val = match[1].replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    const nearby = str.substring(Math.max(0, match.index - 40), Math.min(str.length, match.index + match[0].length + 40));
    if (nearby.includes('"bold"=>true') || nearby.includes('"bold":true')) {
      val = `<strong>${escapeHtml(val)}</strong>`;
    } else if (nearby.includes('"italic"=>true') || nearby.includes('"italic":true')) {
      val = `<em>${escapeHtml(val)}</em>`;
    } else {
      val = escapeHtml(val);
    }
    matches.push(val);
  }
  return matches.length ? `<p>${matches.join('')}</p>` : str;
}

/* ==========================================================================
   17. COHERENT CHROMATIC RECOMMENDATION CARD TINTING
   Extracts genuine dominant hues from each card's individual product photo
   and applies a customized, harmonious glass gradient backdrop.
   Guarantees that no two cards ever share the same gradient or color palette
   by applying golden-ratio chromatic separation and distinct radial orb positions.
   ========================================================================== */
function initRecommendationCardTints() {
  const cards = document.querySelectorAll('.product-card');
  if (!cards.length) return;

  // Distinct radial orb coordinates per card index so geometry differs between cards
  const ORB_POSITIONS = [
    { pos1: '20% 25%', pos2: '80% 75%', pos3: '50% 45%' },
    { pos1: '75% 20%', pos2: '25% 80%', pos3: '55% 50%' },
    { pos1: '50% 15%', pos2: '50% 85%', pos3: '25% 50%' },
    { pos1: '15% 75%', pos2: '85% 25%', pos3: '65% 45%' },
    { pos1: '80% 80%', pos2: '20% 20%', pos3: '60% 40%' },
    { pos1: '30% 20%', pos2: '70% 80%', pos3: '40% 50%' }
  ];

  // Guaranteed distinct vibrant fallback palettes if image colors cannot be extracted
  const DISTINCT_FALLBACKS = [
    [{ r: 34, g: 185, b: 178 }, { r: 245, g: 155, b: 45 }, { r: 40, g: 140, b: 220 }],   // Cyan / Amber / Sky
    [{ r: 236, g: 72, b: 153 }, { r: 59, g: 130, b: 246 }, { r: 139, g: 92, b: 246 }],   // Pink / Blue / Purple
    [{ r: 249, g: 115, b: 22 }, { r: 234, g: 179, b: 8 }, { r: 239, g: 68, b: 68 }],     // Orange / Gold / Red
    [{ r: 16, g: 185, b: 129 }, { r: 6, g: 182, b: 212 }, { r: 99, g: 102, b: 241 }],    // Emerald / Turquoise / Indigo
    [{ r: 168, g: 85, b: 247 }, { r: 236, g: 72, b: 153 }, { r: 245, g: 158, b: 11 }],   // Violet / Magenta / Amber
    [{ r: 14, g: 165, b: 233 }, { r: 52, g: 211, b: 153 }, { r: 244, g: 63, b: 94 }]     // Ocean / Mint / Coral
  ];

  const assignedPrimaryHues = [];

  cards.forEach((card, cardIndex) => {
    const media = card.querySelector('.card-media');
    const img = media ? media.querySelector('img') : null;
    if (!media) return;

    // Apply unique geometry to this card
    const orb = ORB_POSITIONS[cardIndex % ORB_POSITIONS.length];
    media.style.setProperty('--rec-pos1', orb.pos1);
    media.style.setProperty('--rec-pos2', orb.pos2);
    media.style.setProperty('--rec-pos3', orb.pos3);

    // Golden-ratio hue step (~101 deg) guarantees high chromatic contrast between cards
    let cardHueShift = (cardIndex * 0.28 + 0.05) % 1.0;

    const applyCardColors = () => {
      try {
        const src = img ? (img.currentSrc || img.src) : null;
        let baseColors = [];

        if (src) {
          const imgObj = new Image();
          imgObj.crossOrigin = 'anonymous';
          imgObj.onload = () => {
            try {
              baseColors = extractActiveVibrantColors(imgObj, 3);
              finalizeAndSetColors(baseColors);
            } catch (e) {
              finalizeAndSetColors([]);
            }
          };
          imgObj.onerror = () => finalizeAndSetColors([]);
          imgObj.src = src;
        } else {
          finalizeAndSetColors([]);
        }
      } catch (err) {
        finalizeAndSetColors([]);
      }
    };

    const finalizeAndSetColors = (extracted) => {
      let finalColors = [];
      const fallback = DISTINCT_FALLBACKS[cardIndex % DISTINCT_FALLBACKS.length];

      if (extracted && extracted.length > 0) {
        // Base primary color from image palette
        const rootHsl = rgbToHsl(extracted[0]);

        // Shift hue to ensure this card is chromatically distinct from adjacent cards
        let targetHue = (rootHsl.h + cardHueShift) % 1.0;

        // Check against previously assigned hues; push further if too close (< 45 deg / 0.12)
        for (const assigned of assignedPrimaryHues) {
          const diff = Math.min(Math.abs(targetHue - assigned), 1.0 - Math.abs(targetHue - assigned));
          if (diff < 0.12) {
            targetHue = (targetHue + 0.20) % 1.0;
          }
        }
        assignedPrimaryHues.push(targetHue);

        // Derive 3 vibrant harmonious gradient stops
        const c1Hsl = {
          h: targetHue,
          s: Math.max(0.78, Math.min(1.0, rootHsl.s * 1.3)),
          l: Math.max(0.42, Math.min(0.58, rootHsl.l))
        };
        // Secondary stop: triadic/analogous offset +35 deg
        const c2Hsl = {
          h: (targetHue + 0.10) % 1.0,
          s: Math.max(0.75, Math.min(1.0, c1Hsl.s * 1.05)),
          l: Math.max(0.45, Math.min(0.60, c1Hsl.l + 0.05))
        };
        // Tertiary stop: complementary accent +150 deg
        const c3Hsl = {
          h: (targetHue + 0.42) % 1.0,
          s: Math.max(0.80, 1.0),
          l: Math.max(0.44, Math.min(0.56, c1Hsl.l))
        };

        finalColors = [hslToRgb(c1Hsl), hslToRgb(c2Hsl), hslToRgb(c3Hsl)];
      } else {
        // Fallback to guaranteed distinct palette for this card index
        finalColors = fallback;
      }

      const c1 = finalColors[0];
      const c2 = finalColors[1] || finalColors[0];
      const c3 = finalColors[2] || finalColors[1] || finalColors[0];

      media.style.setProperty('--rec-c1', `${c1.r}, ${c1.g}, ${c1.b}`);
      media.style.setProperty('--rec-c2', `${c2.r}, ${c2.g}, ${c2.b}`);
      media.style.setProperty('--rec-c3', `${c3.r}, ${c3.g}, ${c3.b}`);
      media.classList.add('has-card-tint');
    };

    if (img && img.complete && img.naturalWidth > 0) {
      applyCardColors();
    } else if (img) {
      img.addEventListener('load', applyCardColors, { once: true });
    } else {
      finalizeAndSetColors([]);
    }
  });
}

/* ==========================================================================
   18. DYNAMIC SHORT DESCRIPTION INTERACTION
   Smooth hover & click/touch support for short description glassy reveal.
   ========================================================================== */
function initShortDescHover() {
  const containers = document.querySelectorAll('.product-short-desc-hover');
  containers.forEach(el => {
    // Touch/click toggle for mobile and accessibility
    el.addEventListener('click', (e) => {
      // Don't close if clicking inside the panel link/text
      if (e.target.closest('.short-desc-glass-card a')) return;
      el.classList.toggle('is-active');
    });

    // Keyboard trigger
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        el.classList.toggle('is-active');
      } else if (e.key === 'Escape') {
        el.classList.remove('is-active');
      }
    });
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    containers.forEach(el => {
      if (!el.contains(e.target) && !e.target.closest('.buy-header-group')) {
        el.classList.remove('is-active');
      }
    });
  });
}

/* ==========================================================================
   19. USER MANUAL BUTTON SYNC (VISUAL EXPERIENCE METAFIELD)
   If custom.visual_experience.user_manual is provided, ensures link and
   labels are in sync. If no manual is selected (or enabled: false),
   hides the manual button completely so it disappears cleanly.
   ========================================================================== */
function initUserManualSync() {
  const exp = (typeof getVisualExperiencePayload === 'function') ? getVisualExperiencePayload() : null;
  const wrap = document.querySelector('.hotspot-manual-wrap');
  const btn = document.querySelector('.manual-glass-btn');
  const hotspot = document.querySelector('.hotspot');
  if (!exp) return;

  const man = exp.user_manual || exp.manual || exp.pdf_manual;
  if (!man) {
    if (wrap) {
      wrap.style.display = 'none';
      wrap.remove();
    }
    if (hotspot) hotspot.classList.remove('has-manual-btn');
    return;
  }

  let url = '';
  let enabled = true;
  let btnLabel = '';
  let btnSublabel = '';

  if (typeof man === 'string') {
    url = man.trim();
    enabled = (url !== '' && url !== '#');
  } else if (typeof man === 'object') {
    if (typeof man.enabled === 'boolean') enabled = man.enabled;
    url = (man.url || man.src || man.link || '').trim();
    btnLabel = man.button_text || '';
    btnSublabel = man.button_subtext || '';
  }

  if (!enabled || !url || url === '#' || url === '') {
    if (wrap) {
      wrap.style.display = 'none';
      wrap.remove();
    }
    if (hotspot) hotspot.classList.remove('has-manual-btn');
  } else {
    let targetWrap = wrap;
    let targetBtn = btn;
    if (!targetWrap && hotspot) {
      targetWrap = document.createElement('div');
      targetWrap.className = 'hotspot-manual-wrap';
      targetWrap.innerHTML = `
        <a href="${url}" class="manual-glass-btn glass" target="_blank" rel="noopener noreferrer" aria-label="${btnLabel || 'USER MANUAL'}">
          <span class="manual-btn-glow" aria-hidden="true"></span>
          <span class="manual-btn-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </span>
          <span class="manual-btn-text-group">
            <span class="manual-btn-label">${btnLabel || 'USER MANUAL'}</span>
            ${btnSublabel ? `<span class="manual-btn-sublabel">${btnSublabel}</span>` : ''}
          </span>
          <span class="manual-btn-external" aria-hidden="true">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 12L12 4M12 4H6M12 4V10"></path>
            </svg>
          </span>
        </a>
      `;
      hotspot.appendChild(targetWrap);
      targetBtn = targetWrap.querySelector('.manual-glass-btn');
    }
    if (targetWrap) targetWrap.style.display = 'flex';
    if (targetBtn) {
      targetBtn.href = url;
      targetBtn.target = '_blank';
      targetBtn.rel = 'noopener noreferrer';
      if (btnLabel) {
        const lbl = targetBtn.querySelector('.manual-btn-label');
        if (lbl) lbl.textContent = btnLabel;
      }
      if (btnSublabel) {
        let sub = targetBtn.querySelector('.manual-btn-sublabel');
        if (!sub) {
          sub = document.createElement('span');
          sub.className = 'manual-btn-sublabel';
          const grp = targetBtn.querySelector('.manual-btn-text-group');
          if (grp) grp.appendChild(sub);
        }
        if (sub) {
          sub.textContent = btnSublabel;
          sub.style.display = 'block';
        }
      }
    }
    if (hotspot) hotspot.classList.add('has-manual-btn');
  }
}

// Shopify Theme Editor live updates
document.addEventListener('shopify:section:load', () => initProductPage());
document.addEventListener('shopify:section:select', () => initProductPage());
